import React, { useState } from "react";
import styled from "styled-components";
import DeletePromptBG from "../../../assets/images/notesBg.png";
import {
  CorporateTheme,
  CreateFormTheme,
  darkTheme,
} from "../../../utils/themes";
import CloseWhiteIcon from "../../../assets/icons/CloseWhiteIcon";
import CrossIcon from "../../../assets/icons/CrossIcon";
import LoadingCircleIcon from "../../../assets/icons/LoadingCircleIcon";
import ShareLinkIcon from "../../../assets/icons/ShareLinkIcon";
import StyledText from "../../../components/StyledComponents/StyledText";
import CustomInput from "../../../components/form/CustomInput";
import { useToast } from "../../../components/toast/ToastContext";
import PlaceholderImage from "../../../assets/images/placeholder-image.jpg";
import { useTheme } from "../../../middleware/ThemeContext";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const Container = styled.div`
  width: 100%;
  max-width: 600px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.15);
  text-align: center;
  font-family: "Segoe UI", sans-serif;
`;

export const Header = styled.div`
  background-image: url(${DeletePromptBG});
  background-repeat: repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 48px;
`;

export const BigIconWrapper = styled.div`
  background-color: rgba(5, 86, 203, 0.15);
  padding: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Body = styled.div`
  padding: 24px;
  padding-top: 2px;
  display: flex;
  flex-direction: column;
`;

export const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  font-family: "Raleway", sans-serif;
  color: ${CreateFormTheme.colors.primary};
  margin-bottom: 10px;
  margin-top: 30px;
`;

export const Subtitle = styled.p`
  font-size: 14px;
  color: #585558;
  font-weight: 500;
  line-height: 1.5;
  font-family: "Raleway", sans-serif;
  padding-right: 45px;
  padding-left: 45px;
  margin-bottom: 20px;
`;

export const Actions = styled.div`
  display: flex;
  justify-content: end;
  gap: 16px;
  padding: 24px;
`;

export const ActionButton = styled.button<{
  variant: "submit" | "cancel" | "copy";
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 16px;
  height: 42px;
  padding: 7px 16px;
  border: ${({ variant, theme }) =>
    variant === "copy" ? "0px" : `2px solid ${theme.CreateFormTheme.colors.border}`};
  border-radius: ${({ variant }) =>
    variant === "copy" ? "0px 5px 5px 0px" : "6px"};
  cursor: pointer;
  transition: background 0.2s ease;
  background-color: ${({ variant, theme }) =>
    variant === "cancel"
      ? "#f2f2f2"
      : variant === "submit"
      ? theme.colors.mainMenuBackground
      : "#000"};
  color: ${({ variant }) => (variant === "cancel" ? "#222" : "#fff")};

  label {
    margin-top: 2px;
    font-family: "Raleway", sans-serif;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
  }
  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    display: block;
  }

  &:hover {
    opacity: 0.9;
  }
`;

export const CloseIconStyled = styled(CloseWhiteIcon)`
  width: 16px;
  height: 16px;
`;

interface ShareFormModalProps {
  onConfirm: (val: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ShareFormModal: React.FC<ShareFormModalProps> = ({
  onConfirm,
  onCancel,
  loading,
}) => {
  const {theme} = useTheme();
  const showToast = useToast();
  const [formErrors, setFormErrors] = React.useState<{ [key: string]: string }>(
    {}
  );
  const [formData, setFormData] = React.useState<{ [key: string]: any }>({});
  const field = {
    name: "email",
    inputType: "text",
    label: "Email Address",
    placeholder: "Invite through email address",
  };

  const validateForm = () => {
    let errors: { [key: string]: string } = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const shareLink = () => {
    if (!validateForm()) return;
    const value = {
      email: formData?.email,
    };
  };

  return (
    <Overlay>
      <Container>
        <div
          className="d-flex justify-content-end px-2 pt-2 cursor-pointer"
          onClick={onCancel}
        >
          <CrossIcon />
        </div>
        <Header>
          <BigIconWrapper>
            <ShareLinkIcon className="text-primary" size={48} />
          </BigIconWrapper>
          <Title>Share This Form Via Email or Direct link</Title>
          <Subtitle>
            Please copy and share this link with anyone who might be interested
            in filling out the form.
          </Subtitle>
        </Header>
        <Body>
          <StyledText
            className="d-flex align-items-start mb-2"
            textColor={CorporateTheme?.colors?.headerColor}
          >
            Email Address
          </StyledText>
          <div className="d-flex">
            <CustomInput
              value={formData[field.name] || ""}
              onChange={(value) => {
                const processed =
                  field.inputType === "number"
                    ? value.replace(/\D/g, "")
                    : value;
                handleFieldChange(field.name, processed);
              }}
              width="80%"
              className={`${formErrors.email && "border-danger"}`}
              placeholder={field.placeholder}
              type={field.inputType}
              pattern={"[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$"}
            />

            <ActionButton theme={theme} type="button" variant="submit" onClick={shareLink}>
              {loading ? (
                <>
                  <label>Loading...</label>
                  <LoadingCircleIcon />
                </>
              ) : (
                <>
                  <label> Send Invite</label>
                </>
              )}
            </ActionButton>
          </div>

          {formErrors.email && (
            <StyledText
              fontSize="12px"
              className={
                formErrors.email &&
                "text-danger d-flex justify-content-start mt-1"
              }
            >
              {formErrors.email}
            </StyledText>
          )}

          <DashedBorder>
            {[1, 2, 3, 4, 5, 6]?.map((item, ind) => (
              <div className="d-flex align-items-center justify-content-between my-1">
                <ProfileContainer>
                  <ProfileImage $src={PlaceholderImage}>
                    <img
                      src={PlaceholderImage}
                      alt={"img"}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = PlaceholderImage;
                      }}
                    />
                  </ProfileImage>
                  <div className="d-flex flex-column align-items-start">
                    <NameText theme={theme}>{"Eoin" + " " + "Morgan"}</NameText>
                    <IdText theme={theme}> morganeion342@gmail.com</IdText>
                  </div>
                </ProfileContainer>

                <div className="d-flex flex-column">
                  <NameText theme={theme}>Joining Date</NameText>
                  <IdText theme={theme}>12/4/2025</IdText>
                </div>
              </div>
            ))}
          </DashedBorder>

          <div className="d-flex align-items-center justify-content-between border border-1 mt-3 rounded mb-4">
            <StyledText
              className="ms-3"
              fontSize="13px"
              fontWeight={500}
              color={CorporateTheme?.colors?.link}
            >
              https://memberthing/forms/d/e/1FAIpQLSf
            </StyledText>
            <ActionButton theme={theme} variant="copy" onClick={shareLink}>
              <label> Copy Link</label>
            </ActionButton>
          </div>
        </Body>
      </Container>
    </Overlay>
  );
};
export default ShareFormModal;

export const DashedBorder = styled.div`
  border: 1px dashed ${CreateFormTheme.colors?.inputBorders};
  background-color: #f3f5f899;
  padding: 10px;
  border-radius: 4px;
  display: flex;
  margin-top: 20px;
  flex-direction: column;
  max-height: 235px;
  overflow-y: auto;
`;
const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${CorporateTheme.spacing.sm};
`;

export const ProfileImage = styled.div<{ $src?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${CorporateTheme.colors.background};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 50%;
    padding: 2px; /* Creates spacing between image and border */
  }

  &::after {
    content: "";
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid ${CorporateTheme.colors.border};
    border-radius: 50%;
  }
`;

const NameText = styled.span`
  font-weight: 600;
  font-size: 13px;
  font-family: "Raleway", sans-serif;
  color: ${({theme}) => theme.CorporateTheme.colors.textPrimary};
`;

const IdText = styled.span`
  font-size: 12px;
  font-weight: 400;
  font-family: "Lato", sans-serif;
  color: ${({theme}) => theme.CorporateTheme.colors.textSecondary};
  text-align: left;
`;
